import { differenceInCalendarDays } from 'date-fns';
import { createDateSafe } from '@/utils/dateUtils';
import { isAwaitingApproval } from './vacationDpStatus';
import type { VacationDpRow } from '../models/vacationDpBoard.types';
import type { ClientVacationSegment } from '../models/vacationClientView.types';
import type { VacationModality, VacationRequestStatus } from '../models/vacation.types';

/** Mesmo critério de `isAwaitingApproval`: parada em alguma das três mesas de decisão. */
export const PENDING_CLIENT_STATUSES: readonly VacationRequestStatus[] = [
    'PendingManager',
    'PendingClient',
    'PendingDP',
];

export function isPendingStatus(status: VacationRequestStatus): boolean {
    return PENDING_CLIENT_STATUSES.includes(status);
}

/**
 * Uma solicitação candidata a aparecer na aba Cliente: já aprovada (Scheduled/InProgress) ou
 * ainda pendente de decisão (PendingManager/PendingClient/PendingDP). `Draft` e `Completed` nunca
 * chegam aqui — a primeira porque ainda não foi aberta a ninguém, a segunda porque já passou.
 */
export interface ClientRequestCandidate {
    requestId: string;
    status: VacationRequestStatus;
    modality: VacationModality;
    segments: ClientVacationSegment[];
    isPending: boolean;
    requestedAt?: Date | null;
}

/**
 * Junta, para uma linha do painel do DP, as solicitações aprovadas (`approvedPeriods`, já
 * achatadas por segmento) com a solicitação aberta, quando ela está pendente. Reaproveita
 * `isAwaitingApproval` para decidir o que conta como pendente — o mesmo critério do cartão
 * "Aguardando aprovação" do painel do DP.
 */
export function collectClientRequests(row: VacationDpRow): ClientRequestCandidate[] {
    const candidates: ClientRequestCandidate[] = [];
    const seenRequestIds = new Set<string>();

    const requestSegments = new Map<string, ClientVacationSegment[]>();
    row.approvedPeriods?.forEach((period) => {
        if (period.status === 'Completed') return;

        const start = createDateSafe(period.startDate);
        const end = createDateSafe(period.endDate);
        if (!start || !end) return;

        if (!requestSegments.has(period.requestId)) {
            requestSegments.set(period.requestId, []);
        }
        requestSegments.get(period.requestId)!.push({
            startDate: start,
            endDate: end,
            days: differenceInCalendarDays(end, start) + 1,
            order: period.segmentOrder,
        });
    });

    requestSegments.forEach((segments, requestId) => {
        if (segments.length === 0) return;
        segments.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());

        const requestPeriod = row.approvedPeriods?.find((p) => p.requestId === requestId);
        if (!requestPeriod) return;

        const requestedAt = requestPeriod.requestedAt ? createDateSafe(requestPeriod.requestedAt) : null;

        candidates.push({
            requestId,
            status: requestPeriod.status,
            modality: requestPeriod.modality,
            segments,
            isPending: false,
            requestedAt,
        });
        seenRequestIds.add(requestId);
    });

    if (row.request && isAwaitingApproval(row) && !seenRequestIds.has(row.request.id)) {
        const segments: ClientVacationSegment[] = row.request.segments
            .map((segment) => {
                const start = createDateSafe(segment.startDate);
                const end = createDateSafe(segment.endDate);
                if (!start || !end) return null;
                return { startDate: start, endDate: end, days: segment.days, order: segment.order };
            })
            .filter((segment): segment is ClientVacationSegment => segment !== null)
            .sort((a, b) => a.startDate.getTime() - b.startDate.getTime());

        if (segments.length > 0) {
            const requestedAt = row.request.requestedAt ? new Date(row.request.requestedAt.toDate?.() ?? row.request.requestedAt) : null;

            candidates.push({
                requestId: row.request.id,
                status: row.request.status,
                modality: row.request.modality,
                segments,
                isPending: true,
                requestedAt,
            });
        }
    }

    return candidates;
}
